import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearUser } from '../../store/slices/userSlice';
import { theme } from '../../theme/colors';
import './styles/supplier-module.css';

const NAV_ITEMS = [
  { to: '/supplier/dashboard', label: 'Dashboard', icon: 'fa-tachometer' },
  { to: '/supplier/orders', label: 'Purchase Orders', icon: 'fa-file-text-o' },
  { to: '/supplier/catalog', label: 'Catalog', icon: 'fa-list-alt' },
  { to: '/supplier/inventory', label: 'Inventory', icon: 'fa-cubes' },
  { to: '/supplier/restaurants', label: 'Restaurants', icon: 'fa-building-o' },
  { to: '/supplier/profile', label: 'Profile', icon: 'fa-user-o' },
];

const SupplierAppShell: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.user);
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.classList.add('supplier-portal-active');
    return () => document.body.classList.remove('supplier-portal-active');
  }, []);

  const displayName = (() => {
    if (!user) return 'Supplier';
    const name = user.firstName || user.userName;
    if (name?.trim()) return name.trim();
    if (user.emailId) return user.emailId.split('@')[0];
    return 'Supplier';
  })();

  const handleLogout = () => {
    dispatch(clearUser());
    window.location.href = '/logout';
  };

  return (
    <div className="sp-app">
      <aside className={`sp-sidebar${sidebarOpen ? ' sp-sidebar--open' : ''}`} aria-label="Supplier navigation">
        <div className="sp-sidebar__header">
          <div className="sp-sidebar__brand">
            <img
              src={theme.logo.url}
              alt="Peppar"
              className="sp-sidebar__logo"
            />
            <div className="sp-sidebar__brand-text">
              <span className="sp-sidebar__brand-name">Peppar</span>
              <span className="sp-sidebar__brand-tag">Supplier Portal</span>
            </div>
          </div>
          <button
            type="button"
            className="sp-sidebar__close"
            aria-label="Close menu"
            onClick={() => setSidebarOpen(false)}
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>

        <nav className="sp-sidebar__nav">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `sp-sidebar__link${isActive ? ' sp-sidebar__link--active' : ''}`}
            >
              <i className={`fa ${item.icon}`} aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="sp-sidebar__footer">
          <div className="sp-sidebar__user">
            <div className="sp-sidebar__avatar" aria-hidden="true">
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div className="sp-sidebar__user-meta">
              <span className="sp-sidebar__user-name">{displayName}</span>
              <span className="sp-sidebar__user-role">Supplier account</span>
            </div>
          </div>
          <button type="button" className="sp-sidebar__logout" onClick={handleLogout}>
            <i className="fa fa-sign-out" aria-hidden="true" />
            Sign out
          </button>
        </div>
      </aside>

      {sidebarOpen && (
        <button
          type="button"
          className="sp-sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="sp-main">
        <header className="sp-topbar">
          <button
            type="button"
            className="sp-topbar__menu"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="fa fa-bars" aria-hidden="true" />
          </button>
          <div className="sp-topbar__title">Supplier Portal</div>
          <div className="sp-topbar__user">{displayName}</div>
        </header>
        <div className="sp-main__content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default SupplierAppShell;
